import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { HHLinearProgress } from '@hinge-health/react-component-library/dist/components/atoms/hh-linear-progress';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, ButtonBase, Paper, SxProps, Theme, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  phaseOfStep,
  StepId,
  stepIndex,
  StepPhase,
  STEP_LABELS,
  STEP_ORDER,
  STEP_PHASES,
} from '../../constants';
import { useWizardHeader } from '../../context/wizard-header.context';
import { useWizardState } from '../../context/wizard-state.context';
import {
  checkPopKeyframes,
  MOTION_DURATION,
  MOTION_EASING,
  usePrefersReducedMotion,
} from '../motion/motion';
import { WizardContextStrip } from './wizard-context-strip';

type NodeState = 'done' | 'current' | 'unlocked' | 'locked';

const stepFromPath = (pathname: string): StepId | undefined => {
  const last = pathname.split('/').filter(Boolean).pop();
  if (last == null) return undefined;
  return (STEP_ORDER as readonly string[]).includes(last)
    ? (last as StepId)
    : undefined;
};

const phaseProgress = (
  phase: StepPhase,
  currentIdx: number,
  unlockedIdx: number,
): { state: NodeState; done: number; total: number } => {
  const indices = phase.steps.map(s => stepIndex(s));
  const lastIdx = indices[indices.length - 1] ?? 0;
  const done = indices.filter(i => i < currentIdx).length;
  const containsCurrent = indices.includes(currentIdx);
  let state: NodeState;
  if (containsCurrent) state = 'current';
  else if (currentIdx > lastIdx) state = 'done';
  else if (lastIdx <= unlockedIdx) state = 'unlocked';
  else state = 'locked';
  return { state, done, total: phase.steps.length };
};

// Status badge rendered before each phase label. The visual encoding lets
// users read phase state in one glance — color + glyph together signal
// done / current / unlocked / locked without depending on the label text.
const PhaseBadge = ({ state }: { state: NodeState }): JSX.Element => {
  const reduced = usePrefersReducedMotion();
  const base: SxProps<Theme> = {
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: reduced
      ? 'none'
      : `background-color ${MOTION_DURATION.state}ms ${MOTION_EASING.standard}, border-color ${MOTION_DURATION.state}ms ${MOTION_EASING.standard}`,
  };
  const checkAnimation = reduced
    ? 'none'
    : `${checkPopKeyframes} ${MOTION_DURATION.state}ms ${MOTION_EASING.standard} both`;
  if (state === 'done') {
    return (
      <Box sx={{ ...base, bgcolor: 'success.dark' }}>
        <CheckIcon
          sx={{
            color: 'common.white',
            fontSize: 13,
            display: 'block',
            animation: checkAnimation,
          }}
        />
      </Box>
    );
  }
  if (state === 'current') {
    return (
      <Box sx={{ ...base, bgcolor: '#064e3b' }}>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: 'common.white',
          }}
        />
      </Box>
    );
  }
  if (state === 'locked') {
    return (
      <Box sx={{ ...base, border: '1.5px solid', borderColor: 'divider' }}>
        <LockOutlinedIcon
          sx={{ color: 'text.disabled', fontSize: 11, display: 'block' }}
        />
      </Box>
    );
  }
  return (
    <Box sx={{ ...base, border: '1.5px solid', borderColor: 'divider' }} />
  );
};

// Substep visual hierarchy:
// - current  → neutral dark pill (single focal point; contrasts with green checks)
// - done     → green text, transparent (icon carries completion signal)
// - unlocked → primary text, transparent, hover-tinted
// - locked   → muted, no interaction
const subStepSx = (s: NodeState, reduced: boolean): SxProps<Theme> => {
  const shared: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    fontSize: 14,
    lineHeight: 1.2,
    fontFamily: 'inherit',
    borderRadius: 999,
    px: 1.75,
    py: 0.75,
    transition: `background-color ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}, box-shadow ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}, color ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}, transform ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}`,
  };
  // Clickable pills lift a hair on hover — responsive without disturbing the
  // current pill or the row's baseline.
  const hoverLift = reduced ? {} : { transform: 'translateY(-1px)' };
  if (s === 'current') {
    // Deep forest green (emerald-900). Deliberately darker than the
    // bright `success.dark` used for completion marks so the active pill
    // doesn't read as "done".
    return {
      ...shared,
      bgcolor: '#064e3b',
      color: 'common.white',
      fontWeight: 600,
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)',
    };
  }
  if (s === 'done') {
    return {
      ...shared,
      color: 'success.dark',
      fontWeight: 500,
      '&:hover': { bgcolor: 'action.hover', ...hoverLift },
    };
  }
  if (s === 'locked') {
    return {
      ...shared,
      color: 'text.disabled',
      cursor: 'not-allowed',
    };
  }
  return {
    ...shared,
    color: 'text.primary',
    '&:hover': { bgcolor: 'action.hover', ...hoverLift },
  };
};

export const WizardHeaderCard = (): JSX.Element | null => {
  const location = useLocation();
  const navigate = useNavigate();
  const reduced = usePrefersReducedMotion();
  const { state } = useWizardState();
  const { description } = useWizardHeader();
  const current = stepFromPath(location.pathname);
  if (current == null) return null;

  const currentIdx = stepIndex(current);
  const unlockedIdx = stepIndex(state.furthestUnlockedStep);
  const activePhase = phaseOfStep(current);
  const totalSteps = STEP_ORDER.length;
  const percent = Math.round(((currentIdx + 1) / totalSteps) * 100);

  const subStepStateOf = (id: StepId): NodeState => {
    const idx = stepIndex(id);
    if (idx < currentIdx) return 'done';
    if (idx === currentIdx) return 'current';
    if (idx <= unlockedIdx) return 'unlocked';
    return 'locked';
  };

  const handleSubStepClick = (id: StepId, s: NodeState): void => {
    if (s === 'locked') return;
    // WizardHeaderCard is rendered in the `new` layout route's element, so
    // relative paths here resolve from `new` — no `..` needed (and using one
    // would jump up to `contracts/*`, missing every child route).
    navigate(id);
  };

  const subStepIcon = (s: NodeState): JSX.Element | null => {
    if (s === 'done') {
      return (
        <CheckIcon
          sx={{
            color: 'success.dark',
            fontSize: 15,
            display: 'block',
            animation: reduced
              ? 'none'
              : `${checkPopKeyframes} ${MOTION_DURATION.state}ms ${MOTION_EASING.standard} both`,
          }}
        />
      );
    }
    if (s === 'locked') {
      return (
        <LockOutlinedIcon
          sx={{ color: 'text.disabled', fontSize: 14, display: 'block' }}
        />
      );
    }
    return null;
  };

  return (
    <Paper
      elevation={1}
      sx={{
        borderRadius: 3,
        p: 3,
        m: 2,
        backgroundColor: 'background.default',
      }}
    >
      <HHStack spacing={2.5}>
        <HHStack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          spacing={3}
          sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
        >
          <HHTypography
            hhVariant="h1"
            sx={{ fontWeight: 700, lineHeight: 1.15 }}
          >
            {STEP_LABELS[current]}
          </HHTypography>

          <HHStack
            spacing={0.75}
            sx={{ minWidth: 240, flex: '0 1 320px', alignSelf: 'flex-end' }}
          >
            <HHStack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <HHTypography
                hhVariant="variant-bypass"
                sx={{
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 14,
                }}
              >
                Step {currentIdx + 1} of {totalSteps} · {activePhase.label}{' '}
                phase
              </HHTypography>
              <HHTypography
                hhVariant="variant-bypass"
                sx={{
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {percent}%
              </HHTypography>
            </HHStack>
            <HHLinearProgress
              variant="determinate"
              value={percent}
              sx={{
                height: 6,
                borderRadius: 999,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: 'success.dark',
                  // Override MUI's default linear easing so the fill glides
                  // between step percentages with the wizard's ease-out curve.
                  transition: reduced
                    ? 'none'
                    : `transform ${MOTION_DURATION.emphasis}ms ${MOTION_EASING.standard}`,
                },
              }}
            />
          </HHStack>
        </HHStack>

        {description != null && (
          <HHTypography
            hhVariant="body"
            sx={{ color: 'text.secondary', maxWidth: 820 }}
          >
            {description}
          </HHTypography>
        )}

        <HHStack
          spacing={2}
          sx={{
            pt: 2,
            borderTop: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <HHStack
            direction="row"
            alignItems="center"
            sx={{ flexWrap: 'wrap', gap: 0, rowGap: 1.25 }}
          >
            {STEP_PHASES.map((phase, i) => {
              const {
                state: phaseState,
                done,
                total,
              } = phaseProgress(phase, currentIdx, unlockedIdx);
              const prevDone =
                i > 0
                  ? phaseProgress(STEP_PHASES[i - 1], currentIdx, unlockedIdx)
                      .state === 'done'
                  : false;
              const isCurrent = phaseState === 'current';
              return (
                <HHStack
                  key={phase.id}
                  direction="row"
                  alignItems="center"
                  spacing={0}
                >
                  {i > 0 && (
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: 'relative',
                        width: 28,
                        height: 2,
                        mx: 1,
                        borderRadius: 999,
                        bgcolor: 'divider',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Green progress fill sweeps left→right when the
                          preceding phase completes. */}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: 999,
                          bgcolor: 'success.dark',
                          transformOrigin: 'left center',
                          transform: prevDone ? 'scaleX(1)' : 'scaleX(0)',
                          transition: reduced
                            ? 'none'
                            : `transform ${MOTION_DURATION.emphasis}ms ${MOTION_EASING.standard}`,
                        }}
                      />
                    </Box>
                  )}
                  <HHStack direction="row" alignItems="center" spacing={1}>
                    <PhaseBadge state={phaseState} />
                    <HHTypography
                      hhVariant="variant-bypass"
                      sx={{
                        fontWeight: isCurrent ? 600 : 500,
                        color:
                          phaseState === 'locked'
                            ? 'text.disabled'
                            : isCurrent
                              ? 'text.primary'
                              : 'text.secondary',
                        letterSpacing: 0.2,
                        lineHeight: 1.2,
                        fontSize: 14,
                      }}
                    >
                      {phase.label}
                    </HHTypography>
                    {isCurrent && total > 1 && (
                      <Box
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: 'text.secondary',
                          bgcolor: 'action.hover',
                          borderRadius: 999,
                          px: 1,
                          py: 0.25,
                          fontVariantNumeric: 'tabular-nums',
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                          lineHeight: 1.2,
                        }}
                      >
                        {done + 1}/{total}
                      </Box>
                    )}
                  </HHStack>
                </HHStack>
              );
            })}
          </HHStack>

          <HHStack
            direction="row"
            alignItems="center"
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            {activePhase.steps.map(id => {
              const s = subStepStateOf(id);
              const button = (
                <ButtonBase
                  key={id}
                  disableRipple={s === 'locked'}
                  onClick={(): void => handleSubStepClick(id, s)}
                  disabled={s === 'locked'}
                  aria-current={s === 'current' ? 'step' : undefined}
                  sx={subStepSx(s, reduced)}
                >
                  {subStepIcon(s)}
                  {STEP_LABELS[id]}
                </ButtonBase>
              );
              if (s === 'locked') {
                return (
                  <Tooltip
                    key={id}
                    title="Complete the previous step to unlock"
                    placement="top"
                  >
                    <span style={{ display: 'inline-flex' }}>{button}</span>
                  </Tooltip>
                );
              }
              return button;
            })}
          </HHStack>
        </HHStack>

        <WizardContextStrip />
      </HHStack>
    </Paper>
  );
};
